/**
 * What can be said to a live widget from outside it, without knowing anything
 * about the app the widget is in.
 *
 * Everything here reads or drives `window.PrivacyBee` and nothing else, so it
 * needs no host and no framework instance — which is why the auth stores can
 * call it on any cookie-clearing path without caring whether chat is even up.
 */
import type { ChatSdk } from './types';

/**
 * The SDK's body-level host — widget.ts builds `<div id="pb-chat-host">` and
 * attaches its shadow root to it. Its presence is how we know the widget
 * actually mounted, and toggling it is how a caller hides the whole thing.
 */
export const SDK_HOST_ID = 'pb-chat-host';

interface ChatWidgetWindow extends Window {
    PrivacyBee?: ChatSdk;
    PrivacyBeeConfig?: Record<string, string>;
}

/** The browser window, or null on the server — every export here is a no-op there. */
export function chatWidgetWindow(): ChatWidgetWindow | null {
    return typeof window === 'undefined' ? null : (window as unknown as ChatWidgetWindow);
}

/**
 * Take the corner from the SDK, or give it back. One place, one call.
 *
 * Told to the SDK rather than done to its host element: hiding the host from
 * out here leaves the SDK believing it is merely closed, so the next fresh
 * operator reply auto-opens the panel behind `display: none` — and that open
 * flushes the read receipt, telling the server a reply was seen that nobody
 * could see. `setChatSuppressed` shuts the panel, hides the host and disarms
 * auto-open together.
 */
export function setSdkHostVisible(visible: boolean): void {
    const win = chatWidgetWindow();
    if (win === null) return;
    const sdk = win.PrivacyBee;
    if (typeof sdk?.setChatSuppressed === 'function') {
        sdk.setChatSuppressed(!visible);

        return;
    }

    // The published bundle predates setChatSuppressed. Keep the widget off the
    // reserved routes with what the old API offers — the panel can still
    // auto-open unseen behind this until chat-sdk is republished.
    const host = document.getElementById(SDK_HOST_ID);
    if (!host) return;
    if (visible) {
        host.style.removeProperty('display');

        return;
    }
    sdk?.closeChat?.();
    host.style.display = 'none';
    console.warn('[PrivacyBee] chat SDK has no setChatSuppressed(); publish chat-sdk to stop unseen auto-opens');
}

/**
 * Tell a live widget its bearer just changed.
 *
 * ChatSocket captures the token in its constructor and reuses that same value
 * for every reconnect, so once the Passport rotates the widget goes on
 * reconnecting with an expired one. The failure is silent by design — it looks
 * like an ordinary reconnect — and it only surfaces at the next disconnect: a
 * laptop waking, a network change. From then on no replies arrive until the
 * page is reloaded. `notifyLoggedIn()` drops the socket and re-bootstraps
 * identity from the cookie, which is exactly the hand-off needed here.
 *
 * A no-op when the SDK was never loaded, so the auth store can call it on every
 * token write without caring whether chat is up.
 */
export function notifySupportChatIdentityChanged(): void {
    chatWidgetWindow()?.PrivacyBee?.notifyLoggedIn?.();
}

/**
 * Tear the SDK's widget down because the session ended.
 *
 * The launcher and the SDK's widget both survive a client-side navigation, so
 * without this a failed refresh — a logout the member did not choose — lands on
 * the login screen with the previous customer's transcript still on screen and
 * an authenticated socket still open. On a shared computer that is the next
 * person reading it.
 *
 * No-op when chat was never opened, so every cookie-clearing path can call it.
 */
export function tearDownSdkWidget(): void {
    const sdk = chatWidgetWindow()?.PrivacyBee;
    if (sdk === undefined) return;

    if (typeof sdk.resetChat === 'function') {
        sdk.resetChat();

        return;
    }

    // The published bundle predates resetChat. Leaving the widget up through
    // that deploy window is the disclosure this call exists to prevent, so do
    // what can be done from out here: shut the panel and remove the host, which
    // takes the transcript off screen. The socket cannot be closed without the
    // new export and survives until the page unloads — which the logout
    // navigation does moments later.
    sdk.closeChat?.();
    document.getElementById(SDK_HOST_ID)?.remove();
    console.warn('[PrivacyBee] chat SDK has no resetChat(); publish chat-sdk to close the socket on logout');
}

/**
 * Does this raw cookie value represent a signed-in customer?
 *
 * Mirrors `passportSessionToken()` in chat-sdk/src/identity.ts: a value counts
 * only when it decodes as a JWT carrying a `sub`. @nuxtjs/auth writes the
 * literal string `false` into that cookie domain-wide when a refresh fails, so
 * presence alone reads a logged-out browser as a session — and any caller
 * reasoning about which identity the widget will use would then disagree with
 * the widget itself.
 *
 * Pure, so the rule can be tested without a browser or an app instance.
 */
export function isCustomerSessionCookie(raw: string | null): boolean {
    if (raw === null || raw === '') return false;
    const token = raw.startsWith('Bearer ') ? raw.slice('Bearer '.length) : raw;
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    try {
        const claims: unknown = JSON.parse(atob((parts[1] ?? '').replace(/-/g, '+').replace(/_/g, '/')));
        const sub = (claims as { sub?: unknown } | null)?.sub;

        return typeof sub === 'string' && sub !== '';
    } catch {
        return false;
    }
}
